import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { GearItem, GearPal } from "../../types/Pal";
import GearButton from "../../ui/gearbutton";
import GearHeadings from "../../ui/gearheadings";
import {
  IoLocationOutline,
  IoPricetagOutline,
  IoCheckmarkCircleOutline,
  IoPersonOutline,
  IoStarOutline,
  IoMusicalNotesOutline,
  IoMailOutline,
  IoGlobeOutline,
  IoLogoInstagram,
  IoLogoTwitter,
  IoLogoLinkedin,
  IoLogoFacebook,
  IoSendOutline,
  IoInformationCircleOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";

interface GearItemDetailProps {
  item: GearItem;
  pal: GearPal;
  onBack: () => void;
}

const getContactIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return <IoMailOutline className="text-secondary" />;
    case "website":
      return <IoGlobeOutline className="text-secondary" />;
    case "instagram":
      return <IoLogoInstagram className="text-secondary" />;
    case "x":
    case "twitter":
      return <IoLogoTwitter className="text-secondary" />;
    case "linkedin":
      return <IoLogoLinkedin className="text-secondary" />;
    case "facebook":
      return <IoLogoFacebook className="text-secondary" />;
    case "telegram":
      return <IoSendOutline className="text-secondary" />;
    default:
      return <IoInformationCircleOutline className="text-secondary" />;
  }
};

const getContactHref = (type: string, value: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return `mailto:${value}`;
    case "website":
      return value.startsWith("http") ? value : `https://${value}`;
    case "instagram":
      return `https://instagram.com/${value.replace("@", "")}`;
    case "x":
    case "twitter":
      return `https://twitter.com/${value.replace("@", "")}`;
    case "facebook":
      return value.startsWith("http") ? value : `https://facebook.com/${value}`;
    case "linkedin":
      return value.startsWith("http")
        ? value
        : `https://linkedin.com/in/${value}`;
    case "telegram":
      return `https://t.me/${value.replace("@", "")}`;
    default:
      return value.startsWith("http") ? value : "#";
  }
};

const GearItemDetail: React.FC<GearItemDetailProps> = ({
  item,
  pal,
  onBack,
}) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images =
    item.images?.map((img) => img.linkImage) || item.imageUrls || [];
  const hasMultipleImages = images.length > 1;
  console.log(images, item);
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const currentImageUrl = images[currentImageIndex];

  return (
    <div className="space-y-6 animate-scale-in">
      {/* Main Image Carousel */}
      <div className="relative aspect-[16/9] w-full rounded-3xl overflow-hidden bg-white/5 border border-white/10 group">
        {currentImageUrl ? (
          <>
            <img
              src={currentImageUrl}
              alt={`${item.name} - ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-transform duration-500"
            />
            {hasMultipleImages && (
              <>
                {/* Navigation Arrows */}
                <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="p-2 rounded-full bg-black/50 backdrop-blur-md text-white hover:bg-black/70 transition-all border border-white/10"
                  >
                    <IoChevronBack size={20} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="p-2 rounded-full bg-black/50 backdrop-blur-md text-white hover:bg-black/70 transition-all border border-white/10"
                  >
                    <IoChevronForward size={20} />
                  </button>
                </div>

                {/* Indicators / Progress Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 p-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(idx);
                      }}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        idx === currentImageIndex
                          ? "w-4 bg-secondary"
                          : "bg-white/40 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/10 gap-4">
            <IoMusicalNotesOutline size={64} />
            <span className="text-sm font-medium uppercase tracking-widest">
              {t("common.noImage", "Nessuna immagine")}
            </span>
          </div>
        )}
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Location */}
        <div className="bg-[#3A1A60] p-4 rounded-[14px] border border-[#6E11B04D] flex flex-col gap-1">
          <div className="flex items-center gap-2 text-secondary">
            <IoLocationOutline size={18} />
            <span className="text-[14px]">{t("forms.city", "Location")}</span>
          </div>
          <p className="text-white text-[15px] pl-6">
            {pal.city}, {pal.region}
          </p>
        </div>

        {/* Price */}
        <div className="bg-[#3A1A60] p-4 rounded-[14px] border border-[#6E11B04D] flex flex-col gap-1">
          <div className="flex items-center gap-2 text-secondary">
            <IoPricetagOutline size={18} />
            <span className="text-[14px]">{t("booking.price", "Price")}</span>
          </div>
          <p className="text-white text-[15px] pl-6">
            {item.noleggio !== "0.00"
              ? `€${item.noleggio}${t("common.perDay")}`
              : t("common.free")}
          </p>
        </div>

        {/* Availability */}
        <div className="bg-[#3A1A60] p-4 rounded-[14px] border border-[#6E11B04D] flex flex-col gap-1">
          <div className="flex items-center gap-2 text-secondary">
            <IoCheckmarkCircleOutline size={18} />
            <span className="text-[14px]">
              {t("booking.availability", "Availability")}
            </span>
          </div>
          <p className="text-white text-[15px] pl-6">
            {t("booking.availableNow", "Available now")}
          </p>
        </div>

        {/* Owner */}
        <div className="bg-[#3A1A60] p-4 rounded-[14px] border border-[#6E11B04D] flex flex-col gap-1">
          <div className="flex items-center gap-2 text-secondary">
            <IoPersonOutline size={18} />
            <span className="text-[14px]">{t("pal.owner", "Owner")}</span>
          </div>
          <p className="text-white text-[15px] pl-6">{pal.name}</p>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2 px-1">
        <h3 className="text-secondary text-[14px] font-medium">
          {t("forms.description", "Description")
            .replace("(Optional)", "")
            .replace("(optional)", "")
            .replace("(Opzionale)", "")
            .replace("(Opcional)", "")}
        </h3>
        <p className="text-white/80 text-sm leading-relaxed">
          {/* Fallback to subcategory/label if no description */}
          {t("pal.itemDescription", {
            category: item.category,
            subcategory: item.subcategory,
            defaultValue: `${item.category} - ${item.subcategory}`,
          })}
        </p>
      </div>

      {/* Contact Information */}
      {pal.contacts && pal.contacts.length > 0 && (
        <div className="space-y-4 pt-4 border-t border-white/5">
          <h3 className="text-secondary text-[14px]">
            {t("forms.contactInformation", "Informazioni di Contatto")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pal.contacts.map((contact, index) => (
              <a
                key={index}
                href={getContactHref(contact.type, contact.value)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all group"
              >
                <div className="p-2 bg-white/5 rounded-xl group-hover:bg-secondary/10 transition-colors">
                  {getContactIcon(contact.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-bold text-white/30 uppercase tracking-tight leading-none mb-1">
                    {contact.type}
                  </p>
                  <p className="text-sm font-medium text-white/90 truncate group-hover:text-secondary transition-colors">
                    {contact.value}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GearItemDetail;
